import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import type { Appearance, DirectionName, LookManifest, Looks } from "odyssey-shared";
import { GlyphButton, Panel, centreOffset, logical } from "../../ui/kit/index.ts";
import { LazyImages } from "./images.ts";
import { LOOK_PANEL, type PreviewColumn } from "./layout.ts";
import { paintPreview } from "./paint.ts";
import { previewPlan, turnFacing } from "./plan.ts";
import { lookFigure } from "./wording.ts";
import { say } from "../../words.ts";

/**
 * The Look panel's preview (ADR-094 decision 27; the design card `look-step`'s preview panel): the look standing on
 * a 2D canvas, one canvas pixel to one pixel of art, magnified a whole number of logical pixels with no smoothing,
 * and under it the turn row, Turn left and Turn right around the facing's word, each set in the middle of the panel
 * on whole logical pixels; the word's line is the row's height, so its box lies on the grid at any turn size. The left and right arrow keys turn
 * the figure while either button has focus. It draws from the published piece sheets and asks for a sheet only when
 * a look shown needs it. A look is drawn only once every one of its sheets has arrived; until then the figure stays
 * as it was, so no change of a row shows the character without the piece that is loading.
 */

/**
 * What a preview draws from: the page's looks table, the look sheets' pointer it loaded and its asset base.
 */
export interface LookPreviewSource {
  readonly looks: Looks;
  readonly manifest: LookManifest;
  readonly base: string;
}

/**
 * The preview.
 *
 * @param props The `source`, or null on a page whose look sheets did not load, which shows a sentence in the
 *   figure's place; the `look` shown; the `column` it is laid out in; and `touch`, whether the turn buttons are the
 *   touch size.
 * @returns The panel holding the figure and the turn row.
 * @example
 * <LookFigure source={{ looks, manifest, base: "/dist" }} look={look} column={previewColumn(body, previewStage(manifest.layout), false)} touch={false} />
 */
export function LookFigure({ source, look, column, touch }: { readonly source: LookPreviewSource | null; readonly look: Appearance; readonly column: PreviewColumn; readonly touch: boolean }): ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [facing, setFacing] = useState<DirectionName>("down");
  const [arrivals, setArrivals] = useState(0);
  const images = useMemo(() => (source === null ? null : new LazyImages(source.base, () => setArrivals((count) => count + 1))), [source?.base]);
  const plan = useMemo(() => (source === null ? null : previewPlan(source.looks, source.manifest, look, facing)), [source, look.skin, look.hair, look.clothes, facing]);
  const [drawn, setDrawn] = useState(0);

  useEffect(() => {
    const context = canvas.current?.getContext("2d") ?? null;
    if (context === null || plan === null || images === null) {
      return;
    }
    setDrawn(paintPreview(context, plan, images));
  }, [plan, images, arrivals]);

  /**
   * Turns the figure with the left and right arrow keys while a turn button has focus, and keeps the key from the
   * game behind.
   *
   * @param event The key press on the turn row.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }
    event.preventDefault();
    setFacing((now) => turnFacing(now, event.key === "ArrowLeft" ? 1 : -1));
  };

  const turn = touch ? LOOK_PANEL.touchTurn : LOOK_PANEL.turn;
  const word = say(`look.facings.${facing}`);
  return (
    <Panel width={column.width} height={column.height}>
      <div className="ot-look__figure" data-look="figure" style={{ width: logical(column.room.width), height: logical(column.room.height) }}>
        {plan === null ? (
          <p className="ot-look__note" data-look="no-figure">
            {say("look.noFigure")}
          </p>
        ) : (
          <canvas
            ref={canvas}
            className="ot-look__canvas"
            role="img"
            aria-label={lookFigure(facing)}
            data-look="canvas"
            data-facing={facing}
            data-sheets={plan.sheets.map((sheet) => sheet.id).join(" ")}
            data-drawn={drawn}
            data-zoom={column.zoom}
            width={plan.stage.width}
            height={plan.stage.height}
            style={{ width: logical(plan.stage.width * column.zoom), height: logical(plan.stage.height * column.zoom), marginLeft: logical(Math.max(0, centreOffset(column.room.width, plan.stage.width * column.zoom))), marginTop: logical(Math.max(0, centreOffset(column.room.height, plan.stage.height * column.zoom))) }}
          />
        )}
      </div>
      <div className="ot-look__turn" data-look="turn" onKeyDown={onKeyDown} style={{ marginTop: logical(LOOK_PANEL.turnGap), marginLeft: logical(Math.max(0, centreOffset(column.room.width, 2 * turn + 2 * LOOK_PANEL.turnGap + LOOK_PANEL.facing))), gap: logical(LOOK_PANEL.turnGap), height: logical(turn) }}>
        <GlyphButton glyph="chevron-left" label={say("look.turnLeft")} size={turn} onClick={() => setFacing((now) => turnFacing(now, 1))} data={{ "data-look": "turn-left" }} />
        <span className="ot-look__facing" data-look="facing" aria-live="polite" style={{ width: logical(LOOK_PANEL.facing), height: logical(turn), lineHeight: logical(turn) }}>
          {word}
        </span>
        <GlyphButton glyph="chevron-right" label={say("look.turnRight")} size={turn} onClick={() => setFacing((now) => turnFacing(now, -1))} data={{ "data-look": "turn-right" }} />
      </div>
    </Panel>
  );
}
